import { useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

import { useUsers } from "../../api/hooks.ts";
import { Icon } from "../../ui/Icon.tsx";
import { ownerFromInput, shortOwnerId, useOwnerSelection } from "../auth/owner.ts";
import { useOwnerLabel } from "../auth/ownerLabel.ts";
import { usePermissions } from "../auth/permissions.ts";
import { sectionUrl } from "../library/section.ts";

/**
 * EL SELECTOR DE DUENO de la topbar (ADR 0005): de quien es la bitacora que se
 * esta mirando. Solo existe para quien puede mirar la de otro —Administrador y
 * Mentor—; para un Trader no se pinta nada, porque el servidor le respondera
 * 404 a cualquier `?owner=` que no sea el suyo.
 *
 * Es un `<select class="ctl">`, el mismo control de los formularios de la
 * plantilla, y no un menu propio: en el movil abre el selector nativo, que es
 * el unico que cabe con el pulgar en una barra de 58 px.
 *
 * "Mi bitacora" va primero y es el valor sin dueno. Las demas opciones son los
 * usuarios ACTIVOS, que son los que tienen una bitacora viva que mirar; los
 * desactivados siguen teniendo la suya, y se llega a ella por el enlace, no por
 * la lista.
 *
 * LO QUE EL MENTOR NO TIENE: la lista. `GET /users` es solo del Administrador
 * (la matriz del ADR 0005 no le da a nadie mas ni verla), asi que un Mentor no
 * puede elegir un nombre de un desplegable. Lo que si puede es abrir el enlace
 * que le compartieron o pegar el identificador: la opcion "Otra bitacora…"
 * abre esa casilla en la cinta de abajo. Si esto llega a doler, lo que hay que
 * cambiar es el CONTRATO —un listado de duenos legible por quien puede leer la
 * de otro—, no este selector.
 */

const MINE = "";
const BY_ID = "__by-id";

/**
 * SOCIAL DATA, segunda del desplegable. Lo pidio el propietario asi: «tambien lo
 * quiero en el desplegable donde esta Mi bitacora y donde salen los usuarios, pon
 * que siempre Mi bitacora sea el primero del desplegable, social data el segundo
 * y los demas usuarios despues».
 *
 * NO ES UN DUEÑO, ES UN DESTINO, y por eso no se queda seleccionada: al elegirla
 * se navega y el control vuelve a mostrar de quien es la bitacora. Este
 * desplegable es lo UNICO que avisa de que estas mirando la de otro -- junto con
 * la cinta de abajo, que se pinta a partir de el --, y si se quedara diciendo
 * "Social Data" esa señal desapareceria justo cuando mas falta hace.
 */
const SOCIAL = "__social";

/** La ruta del detalle de una operacion, que es de un dueno concreto. */
const TRADE_DETAIL = /^\/trades\/(?!new$)[^/]+$/;

export function OwnerSwitch(props: {
  askingId: boolean;
  onAskId: (asking: boolean) => void;
}): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const { canReadOthers, isAdministrator, user: me } = usePermissions();
  const { ownerId, setOwner } = useOwnerSelection();
  /* El Mentor no puede listar: la peticion no se hace, y no es un 403 que se
     esconde sino uno que no se provoca. */
  const users = useUsers(isAdministrator && canReadOthers);

  if (!canReadOthers) return null;

  const others = (users.data ?? [])
    .filter((item) => item.isActive && item.id !== me?.id)
    .sort((left, right) => left.displayName.localeCompare(right.displayName, "es"));

  const known = ownerId !== undefined && others.some((item) => item.id === ownerId);
  const value = props.askingId ? BY_ID : (ownerId ?? MINE);

  function choose(next: string): void {
    if (next === BY_ID) {
      props.onAskId(true);

      return;
    }

    /* Un destino, no un dueño: lleva alli y el desplegable se queda como estaba,
       diciendo de quien es la bitacora. El dueño elegido no se toca, asi que
       Social Data se abre con la bitacora que ya estabas mirando. */
    if (next === SOCIAL) {
      navigate(sectionUrl("social"));

      return;
    }

    props.onAskId(false);

    /* Desde el detalle de una operacion se vuelve a la bitacora: esa operacion
       es del dueno anterior y con el nuevo seria un 404. */
    const target = TRADE_DETAIL.test(location.pathname) ? "/trades" : undefined;

    setOwner(next === MINE ? undefined : next, target);
  }

  return (
    <select
      className={ownerId === undefined ? "ctl owner-select" : "ctl owner-select other"}
      aria-label="Bitacora que estas mirando"
      title="Bitacora que estas mirando"
      value={value}
      onChange={(event) => {
        const chosen = event.target.value;

        /* SE DEVUELVE EL VALOR A MANO, y no es un adorno: este `select` lo
           controla el DUEÑO, y "Social Data" no cambia ningun dueño. Sin esto,
           elegirlo estando ya en Social Data no cambiaria nada de lo que React
           observa, no habria repintado, y el control se quedaria diciendo
           "Social Data" — o sea, dejando de decir de quien es la bitacora, que
           es su unico trabajo. Se repone ANTES de navegar y se despacha lo que
           se eligio, no lo que quedo en el elemento. */
        if (chosen === SOCIAL) {
          event.target.value = ownerId ?? MINE;
        }

        choose(chosen);
      }}
    >
      <option value={MINE}>Mi bitacora</option>
      <option value={SOCIAL}>Social Data</option>
      {others.map((item) => (
        <option key={item.id} value={item.id}>
          {item.displayName}
        </option>
      ))}
      {/* Un dueno que llego por enlace y no esta en la lista —o no hay lista,
          como le pasa al Mentor— se ensena igual, con lo unico que se sabe de
          el: su identificador. */}
      {ownerId !== undefined && !known && (
        <option value={ownerId}>Bitacora {shortOwnerId(ownerId)}</option>
      )}
      {!isAdministrator && <option value={BY_ID}>Otra bitacora…</option>}
    </select>
  );
}

/**
 * LA CINTA bajo la topbar: dice de quien es lo que se esta viendo y devuelve a
 * la propia con un toque. Es informativa, no una alarma: el ambar de esta
 * aplicacion mide disciplina, y esto no es una regla rota, es un contexto que
 * conviene no perder de vista mientras se lee.
 *
 * Con `askingId`, en vez del nombre trae la casilla para pegar el enlace o el
 * identificador (ver `OwnerSwitch`).
 */
export function OwnerRibbon(props: {
  askingId: boolean;
  onAskId: (asking: boolean) => void;
}): ReactNode {
  const { setOwner } = useOwnerSelection();
  const owner = useOwnerLabel();

  if (props.askingId) {
    return <ByIdForm onClose={() => props.onAskId(false)} />;
  }

  if (!owner.viewingOther) return null;

  return (
    <div className="owner-ribbon" role="status">
      <Icon name="eye" />
      <span>
        Estas mirando la bitacora de <b>{owner.label}</b>. Solo lectura: lo que se registra
        desde aqui iria a la tuya, asi que no se ofrece.
      </span>
      <button type="button" className="btn ghost sm" onClick={() => setOwner(undefined)}>
        <Icon name="x" />
        Volver a la mia
      </button>
    </div>
  );
}

function ByIdForm(props: { onClose: () => void }): ReactNode {
  const { setOwner } = useOwnerSelection();
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState(false);

  function open(event: FormEvent): void {
    event.preventDefault();

    const ownerId = ownerFromInput(text);

    if (ownerId === undefined) {
      setInvalid(true);

      return;
    }

    props.onClose();
    setOwner(ownerId, "/trades");
  }

  return (
    <form className="owner-ribbon" onSubmit={open}>
      <label className="owner-byid">
        <span>Pega el enlace o el identificador que te compartieron</span>
        <input
          className="ctl"
          value={text}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          placeholder="https://… ?owner=… o el identificador"
          aria-invalid={invalid}
          onChange={(event) => {
            setText(event.target.value);
            setInvalid(false);
          }}
        />
      </label>
      {invalid && (
        <span className="err" role="alert">
          Eso no es un identificador ni un enlace con <code>?owner=</code>.
        </span>
      )}
      <div className="owner-byid-actions">
        <button type="button" className="btn ghost sm" onClick={props.onClose}>
          Cancelar
        </button>
        <button type="submit" className="btn primary sm">
          <Icon name="eye" />
          Abrir
        </button>
      </div>
    </form>
  );
}
